import { useEffect, useState } from "react";
import useAxios from "./useAxios";

/** Field-level summary of one index for one acquisition, from `satellite-data/field-timeseries/<id>/`. */
export interface SatelliteStatPoint {
  satellite_data_id: number;
  request_id: number;
  acquisition_date: string;
  mean: number | null;
  median: number | null;
  std: number | null;
  p10: number | null;
  p90: number | null;
  min: number | null;
  max: number | null;
  valid_pixels: number;
  field_pixels: number | null;
  /** Share of the field that was cloud free (0–1), when known. */
  coverage: number | null;
}

export type SatelliteSeries = Record<string, SatelliteStatPoint[]>;

export const validSeriesPoints = (points: SatelliteStatPoint[] | undefined) =>
  (points ?? [])
    .filter((point) => point.mean !== null && Number.isFinite(point.mean))
    .sort((a, b) => Date.parse(a.acquisition_date) - Date.parse(b.acquisition_date));

/**
 * Loads field-average index values over time for every index of a field.
 * Change `refreshKey` to refetch, e.g. when new observations have been loaded.
 */
export function useSatelliteTimeSeries(fieldId: number | null | undefined, refreshKey?: unknown) {
  const api = useAxios();
  const [series, setSeries] = useState<SatelliteSeries>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fieldId || !Number.isFinite(fieldId)) {
      setSeries({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`satellite-data/field-timeseries/${fieldId}/`)
      .then((res) => {
        if (!cancelled) setSeries(res.data?.series ?? {});
      })
      .catch((err) => {
        if (cancelled) return;
        setSeries({});
        setError(err?.response?.status ? `HTTP ${err.response.status}` : "Request failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, refreshKey]);

  return { series, loading, error };
}
